<template>
    <div class="app">
        <div
            v-if="isAuthenticated"
            class="app-layout"
        >
            <Sidebar
                :user-name="userName"
                @logout="logout"
            />
            <main class="main-content">
                <router-view v-slot="{ Component, route }">
                    <KeepAlive
                        v-if="shouldKeepAlive(route.name)"
                        ref="keepAliveRef"
                    >
                        <component
                            :is="Component"
                            :key="getKeepAliveKey(route)"
                            :userName="userName"
                            :userEmail="userEmail"
                            @logout="logout"
                        />
                    </KeepAlive>
                    <component
                        v-else
                        :is="Component"
                        :key="route.path"
                        :userName="userName"
                        :userEmail="userEmail"
                        @logout="logout"
                    />
                </router-view>
            </main>
        </div>
        <router-view v-else-if="isUnauthorizedRoute" />
        <SignInPage
            v-else-if="authChecked"
            :loading="signingIn"
            :error="signInError"
            @sign-in="signIn"
        />
        <!-- Legacy toast component for notifications -->
        <LegacyToast position="bottom-right" />

        <!-- Design system toast component -->
        <Toaster />

        <!-- Global Search Component -->
        <GlobalSearch ref="globalSearchRef" />
        <!-- Staging Banner (only renders when isStaging is true) -->
        <StagingBanner />
        <!-- Global floating dev tools (only renders when isStaging is true) -->
        <DevToolbar v-if="isAuthenticated" />
    </div>
</template>

<script lang="ts">
import {
    GoogleAuthProvider,
    signInWithRedirect,
    signInWithPopup,
    getAuth,
    Auth,
} from 'firebase/auth'
import LegacyToast from 'primevue/toast'
import Sidebar from './components/Sidebar.vue'
import GlobalSearch from './components/GlobalSearch.vue'
import StagingBanner from './components/ui/StagingBanner.vue'
import DevToolbar from './components/DevToolbar.vue'
import SignInPage from './pages/SignInPage.vue'
import { usePreset } from '@primevue/themes'
import { baseTheme } from './theme'
import { littaPreset } from './components/commercial-accounts/detail/theme'
import { Toaster } from '@LITTA-CO/ui-vue'

import { getFirebaseDocument, isQA, isStaging } from './modules/firebase'
import {
    isAllowedCompanyEmail,
    shouldUsePopupSignIn,
    describeSignInError,
} from './modules/auth'

const LITTA_THEME_ROUTES = [
    'commercial-account',
    'tools-event-relays',
    'gateway',
]

function isLittaThemeRoute(name: string | symbol | null | undefined): boolean {
    return (
        typeof name === 'string' &&
        LITTA_THEME_ROUTES.some(route => name.startsWith(route))
    )
}

export default {
    name: 'App',
    components: {
        Toaster,
        LegacyToast,
        Sidebar,
        GlobalSearch,
        StagingBanner,
        DevToolbar,
        SignInPage,
    },
    data() {
        return {
            isAuthenticated: false,
            authChecked: false,
            signingIn: false,
            signInError: null as null | string,
            userName: null as null | string,
            userEmail: null as null | string,
            auth: null as null | Auth,
            // Routes that should be excluded from KeepAlive
            routesToExcludeFromKeepAlive: [
                'assignment',
                'skips',
                'skips-movements',
                'commercial-accounts',
                'commercial-account-detail',
                'partner-detail',
                'image-analysis',
            ] as string[],
        }
    },
    methods: {
        logout() {
            if (this.auth) this.auth.signOut()
        },
        async signIn() {
            if (!this.auth || this.signingIn) return
            this.signInError = null
            this.signingIn = true
            const provider = new GoogleAuthProvider()
            try {
                if (shouldUsePopupSignIn(window.location.hostname)) {
                    // onAuthStateChanged handles success.
                    await signInWithPopup(this.auth, provider)
                } else {
                    await signInWithRedirect(this.auth, provider)
                }
            } catch (err) {
                this.signInError = describeSignInError(err)
            } finally {
                this.signingIn = false
            }
        },
        shouldKeepAlive(
            routeName: string | symbol | null | undefined,
        ): boolean {
            if (!routeName) return true
            return !this.routesToExcludeFromKeepAlive.includes(
                routeName as string,
            )
        },
        getKeepAliveKey(route: {
            name?: string | symbol | null
            path: string
            fullPath: string
            meta?: {
                reuseOnParamChange?: boolean
                reuseOnQueryChange?: boolean
            }
        }) {
            if (route.meta?.reuseOnParamChange) return String(route.name)
            if (route.meta?.reuseOnQueryChange) return route.path
            return route.fullPath
        },
        logKeepAliveInfo() {
            if (this.$refs.keepAliveRef) {
                console.log('KeepAlive ref:', this.$refs.keepAliveRef)
                console.log(
                    'KeepAlive cached components:',
                    // @ts-ignore - KeepAlive internal properties
                    this.$refs.keepAliveRef.cached,
                )
            }
        },
    },
    created() {
        console.log('GETTING AUTH')
        const auth = getAuth()
        console.log('SETTING AUTH')
        this.auth = auth
        console.log('REGISTERING ONAUTHSTATECHANGED OBSERVER')
        auth.onAuthStateChanged(user => {
            this.authChecked = true

            if (!user) {
                // Signed out: render the sign-in page. We don't auto-trigger
                // sign-in here because popups must open from a user gesture.
                this.isAuthenticated = false
                this.userName = null
                this.userEmail = null
                return
            }

            const isCompanyEmail = isAllowedCompanyEmail(user?.email)
            if (isCompanyEmail) {
                if (this.isAuthenticated) return
                this.isAuthenticated = true
                this.userName = user?.displayName
                this.userEmail = user.email
                return
            }

            // Signed in but not a company account.
            this.isAuthenticated = false
            this.userName = null
            this.userEmail = null
            if (this.$route?.name !== 'unauthorized') {
                this.$router.replace({ name: 'unauthorized' })
            }
            this.auth?.signOut()
        })

        // Apply Litta theme if landing directly on a branded route
        const routeName = this.$route?.name
        if (isLittaThemeRoute(routeName)) {
            usePreset(littaPreset)
        }
    },
    computed: {
        isUnauthorizedRoute() {
            return this.$route?.name === 'unauthorized'
        },
        paramId() {
            return this.$route.params?.id as string
        },
        currentPath() {
            return this.$route.path
        },
    },
    watch: {
        $route(to, from) {
            // Reset preset to baseTheme when leaving quotebuilder route
            if (from?.name === 'quotebuilder' && to?.name !== 'quotebuilder') {
                usePreset(baseTheme)
            }

            // Apply Litta theme on Litta-branded routes; fully replace on exit so
            // component-level overrides (e.g. togglebutton checkedBackground) don't
            // leak into other pages — updatePreset deep-merges, usePreset replaces.
            if (isLittaThemeRoute(to?.name) && !isLittaThemeRoute(from?.name)) {
                usePreset(littaPreset)
            } else if (
                !isLittaThemeRoute(to?.name) &&
                isLittaThemeRoute(from?.name)
            ) {
                usePreset(baseTheme)
            }

            // Reset global styles when leaving view route
            if (from?.name === 'view' && to?.name !== 'view') {
                // Use setTimeout to ensure cleanup happens after Vue processes the route change
                setTimeout(() => {
                    const appElement = document.getElementById('app')
                    if (appElement) {
                        // Reset overflow style - force removal
                        appElement.style.removeProperty('overflow')
                        appElement.style.overflow = ''

                        // Remove hidden class from ALL elements that might have it
                        const viewContainers = appElement.querySelectorAll(
                            '.view-container.hidden, .container.hidden, .hidden',
                        )
                        viewContainers.forEach(container => {
                            container.classList.remove('hidden')
                        })

                        // Force a reflow to ensure styles are recalculated
                        void appElement.offsetHeight

                        // Force recalculation of Tailwind classes
                        requestAnimationFrame(() => {
                            void appElement.offsetHeight
                        })
                    }
                }, 0)
            }

            this.$nextTick(() => {
                this.logKeepAliveInfo()
            })
        },
        async currentPath() {
            // If running on localhost or staging/QA, do not redirect
            if (
                window.location.hostname === 'localhost' ||
                window.location.hostname === '127.0.0.1' ||
                isStaging ||
                isQA
            ) {
                return
            }

            // Redirect view routes with id parameter based on isV2 status
            if (this.$route.name === 'view' && this.$route.params?.id) {
                const { id } = this.$route.params
                const actualId = Array.isArray(id) ? id[0] : id

                if (actualId) {
                    console.log('Getting document for view route', actualId)
                    const doc = await getFirebaseDocument(
                        'book_orders',
                        actualId as string,
                    )

                    console.log(
                        'isV2 check for view route',
                        actualId,
                        'isV2:',
                        doc?.isV2,
                        'doc:',
                        doc,
                    )

                    if (!doc?.isV2) {
                        console.log('isV2 is false, redirecting to old beehive')
                        const currentUrl = new URL(window.location.href)
                        // Redirect to the old beehive domain
                        currentUrl.hostname = 'old.beehive.litta.co'
                        currentUrl.port = ''
                        window.location.href = currentUrl.toString()
                    }
                }
            }
        },
    },
}
</script>

<style scoped>
.app {
    position: relative;
    height: 100%;
    overflow: hidden;
}

.app-layout {
    display: flex;
    height: 100%;
    width: 100%;
}

.main-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-gutter: stable;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.35s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0 !important;
}
</style>
